import { Directive, ElementRef, Input, NgZone, OnDestroy, OnInit } from '@angular/core';

/**
 * Wiersze listy wchodzą jeden po drugim, zamiast pojawiać się wszystkie naraz.
 *
 * Dyrektywa na KONTENERZE, nie na pozycji: `<tbody appStaggerIn>` zamiast atrybutu przy
 * każdym `*ngFor`. Jedno wpięcie na listę zamiast jednego na wiersz, a przy okazji nie
 * trzeba nigdzie przekazywać indeksu — kolejność bierze się z DOM-u.
 *
 * Nowe dzieci wyłapuje `MutationObserver`, a nie `ngAfterViewChecked`: ten drugi biega
 * przy każdej detekcji zmian, więc przy liście na kilkaset wierszy płaciłby za przejście
 * po nich wielokrotnie na sekundę. Obserwator budzi się wyłącznie wtedy, gdy coś
 * naprawdę doszło, i pracuje poza strefą Angulara — pisze do stylu, więc detekcja zmian
 * nie ma tu czego szukać.
 *
 * Każdy element animuje się RAZ. Bez tego doklejenie kolejnej porcji przy przewijaniu
 * odtwarzałoby animację wierszy, które użytkownik właśnie czyta — a to wygląda jak
 * awaria, nie jak efekt.
 *
 * Sam wygląd wejścia siedzi globalnie w `styles.scss` jako `.stagger-in`, razem
 * z wyłącznikiem dla `prefers-reduced-motion` (WCAG 2.3.3).
 */
@Directive({
  selector: '[appStaggerIn]'
})
export class StaggerInDirective implements OnInit, OnDestroy {

  /**
   * Wyłącznik kaskady.
   *
   * Schodek jest po to, żeby PIERWSZE zetknięcie z listą dało oku kierunek czytania.
   * Przy kolejnej stronie użytkownik wie już, gdzie patrzeć, więc kaskada zostaje samym
   * opóźnieniem między kliknięciem a treścią — widok, który stronicuje, gasi ją wtedy
   * sam:
   *
   *     <div class="alarm-cards" [appStaggerIn]="staggersIn(sos)">
   *
   * Wyłącza WYŁĄCZNIE jawne `false`. Pusty atrybut (`appStaggerIn`) daje `''`, a widok,
   * który nie zna jeszcze swojego stanu, bywa `null` albo `undefined` — w obu wypadkach
   * kaskada ma grać, inaczej każde z kilkudziesięciu użyć musiałoby przekazywać `true`.
   */
  @Input('appStaggerIn') enabled: boolean | '' = '';

  /** Odstęp między kolejnymi pozycjami w milisekundach. */
  @Input() staggerStep = 40;

  /**
   * Po ilu pozycjach opóźnienie przestaje rosnąć.
   *
   * Bez tego limitu setny wiersz czekałby cztery sekundy — użytkownik zdążyłby do niego
   * przewinąć, zanim by się pojawił, i zobaczyłby puste miejsce zamiast danych.
   */
  @Input() staggerMax = 12;

  /**
   * Skąd pozycje wchodzą. Domyślnie od dołu — tak, jak rosną listy dopisywane na końcu.
   *
   * `top` dla list, do których nowe pozycje dokładają się na WIERZCH (lista rozmów
   * czatu): ruch w tę samą stronę czyta się jak dosypywanie, a wypychanie od dołu
   * przeczyłoby temu, co lista właśnie robi.
   *
   * `right` dla rzeczy przyklejonych do prawej krawędzi ekranu (miniaturki doku czatu):
   * wjazd z boku czyta się jak wysunięcie zza krawędzi, a ruch w pionie sugerowałby
   * listę, którą one nie są.
   */
  @Input() staggerFrom: 'bottom' | 'top' | 'right' = 'bottom';

  /**
   * Elementy, które już weszły. `WeakSet`, a nie tablica: wiersz usunięty z DOM-u ma
   * zniknąć też stąd, bez naszego udziału — inaczej długo otwarta lista trzymałaby przy
   * życiu każdy wiersz, jaki się na niej kiedykolwiek pojawił.
   */
  private readonly revealed = new WeakSet<Element>();

  private observer: MutationObserver = null;

  constructor(private host: ElementRef<HTMLElement>, private ngZone: NgZone) {}

  ngOnInit(): void {
    // Pozycje wyrenderowane zanim obserwator ruszył — pierwsza porcja listy zwykle
    // jest już w DOM-ie, gdy dyrektywa się inicjalizuje.
    this.revealNew();

    if (typeof MutationObserver === 'undefined') {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => this.revealNew());
      this.observer.observe(this.host.nativeElement, { childList: true });
    });
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  /**
   * Nadaje opóźnienie każdemu dziecku, które jeszcze nie weszło. Licznik startuje od zera
   * dla KAŻDEJ porcji, więc doklejone wiersze wchodzą własną kaskadą, a nie po opóźnieniu
   * odziedziczonym po tych, które już stoją na ekranie.
   *
   * Kaskada biegnie w tę samą stronę co ruch: przy wjeździe z prawej krawędzi pierwsze
   * rusza dziecko OSTATNIE, czyli to stojące najbliżej niej. Kolejność z DOM-u dawałaby
   * tam kaskadę od lewej, więc pod prąd temu, skąd elementy nadjeżdżają.
   */
  private revealNew(): void {
    const fresh = this.freshChildren();
    const order = this.staggerFrom === 'right' ? fresh.slice().reverse() : fresh;

    order.forEach((element, inBatch) => {
      this.revealed.add(element);

      // Kaskada zgaszona: pozycję ODNOTOWUJEMY, ale jej nie animujemy. Bez tego
      // ponowne włączenie kazałoby wejść od nowa wierszom, które już stoją na ekranie.
      if (this.enabled === false) {
        return;
      }

      const steps = Math.min(inBatch, Math.max(this.staggerMax, 0));
      element.style.animationDelay = `${steps * this.staggerStep}ms`;
      element.classList.add('stagger-in');
      if (this.staggerFrom !== 'bottom') {
        element.classList.add('stagger-in-' + this.staggerFrom);
      }
    });
  }

  /** Dzieci, które jeszcze nie weszły — w kolejności DOM-u. */
  private freshChildren(): HTMLElement[] {
    const children = this.host.nativeElement.children;
    const fresh: HTMLElement[] = [];

    for (let i = 0; i < children.length; i++) {
      const element = children[i] as HTMLElement;
      if (!this.revealed.has(element)) {
        fresh.push(element);
      }
    }

    return fresh;
  }
}
